/*
 * VT-joukkokirjaus ja pikakirjaus (Tonin havainto 11.10.2026 «Visuaalinen tarkistaminen on hitokseen hankalan mutkan takana…
 * vois olla hitseille VT-ruksi ja "valitse kaikki"»; pääChatin suunnitelma).
 *
 * Kolme osaa, yksi palvelimen reitti (POST /api/hitsit/vt):
 *  - VtSolu: rivin ensimmäinen sarake — valintaruutu sekä «✓» ja «✗». Sarake on rivin ALUSSA, jotta se näkyy puhelimessa
 *    (390 px) ilman taulukon vieritystä; VT tehdään linjalla.
 *  - VtHylkays: «✗» avaa rivin alle vikatyypin ja kuvauksen (toinen napautus tallentaa).
 *  - VtJoukkoIkkuna: «Kirjaa VT valituille» — tarkastaja, päivä, pöytäkirja ja jokaisen hitsin tulos (oletus hyväksytty).
 * Hitsaamatonta ja jo VT-kirjattua ei voi valita; palvelin ohittaa ne silti ja kertoo nimeltä (ei luoteta selaimeen).
 */
import { useEffect, useRef, useState } from "react";
import { api, ApiError, type Hitsi, type Kayttaja } from "./api";
import { naytaHitsinumero } from "./HitsiTunnus";
import { t, virheTeksti } from "./kieli";
import type { KaannosAvain } from "./kaannokset";
import { PoytakirjaLiite } from "./PoytakirjaLiite";

export const VT_VIAT = ["reunahaava", "huokonen", "roiske", "kupu", "vajaa_taytto", "halkeama", "muu"] as const;
export type VtVika = typeof VT_VIAT[number];

/** Hitsi on hitsattu (ei suunniteltu eikä «ei toteudu») — sama ehto kuin palvelimella. */
export const vtHitsattu = (h: Hitsi) => h.vaihe !== "suunniteltu" && !h.ei_toteudu;
/** Hitsillä on jo VT (mikä tahansa tulos). */
export const vtKirjattu = (h: Hitsi) => h.tarkastukset.some((x) => x.menetelma === "VT");
/** Voiko hitsin valita joukkokirjaukseen tai pikakirjata. */
export const vtValittavissa = (h: Hitsi) => vtHitsattu(h) && !vtKirjattu(h);
/** Viimeisin VT on hylätty → rivillä näkyy «VT hylätty» ja syy. */
export const vtHylatty = (h: Hitsi) => {
  const vt = h.tarkastukset.filter((x) => x.menetelma === "VT");
  const viim = vt[vt.length - 1];
  return viim && viim.tulos === "hylatty" ? viim : null;
};
const tanaan = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Helsinki" });
const vikaNimi = (v: VtVika) => t(`vt.vika.${v}` as KaannosAvain);

export function VtSolu({ h, valittu, vaihda, hyvaksy, hylkaa, kaynnissa, ndt }: {
  h: Hitsi; valittu: boolean; vaihda: () => void; hyvaksy: () => void; hylkaa: () => void; kaynnissa: boolean;
  /** T525: avaa NDT-tuloksen kirjauksen rivin alle (hitsatulle hitsille). */ ndt?: () => void;
}) {
  const ok = vtValittavissa(h);
  const syy = !vtHitsattu(h) ? t("vt.ei_hitsattu") : vtKirjattu(h) ? t("vt.jo_kirjattu") : undefined;
  return (
    <td className="vt-solu" data-vt-solu={h.id} data-vt-valittavissa={ok ? "1" : "0"}>
      <input type="checkbox" checked={valittu} disabled={!ok} onChange={vaihda} title={syy ?? t("vt.valitse_title")}
             aria-label={t("vt.valitse_title")} data-vt-valinta={h.id} />
      {ok && <>
        <button type="button" className="vt-nappi vt-nappi--ok" disabled={kaynnissa} onClick={hyvaksy}
                title={t("vt.hyvaksy_title", { nro: h.hitsinumero })} data-vt-hyvaksy={h.id}>VT ✓</button>
        <button type="button" className="vt-nappi vt-nappi--hyl" disabled={kaynnissa} onClick={hylkaa}
                title={t("vt.hylkaa_title", { nro: h.hitsinumero })} data-vt-hylkaa={h.id}>✗</button>
      </>}
      {ndt && vtHitsattu(h) && (
        <button type="button" className="vt-nappi" onClick={ndt} title={t("ndt.avaa_title", { nro: h.hitsinumero })}
                data-ndt-avaa={h.id}>{t("ndt.avaa")}</button>
      )}
    </td>
  );
}

/* «✗» avaa rivin hylkäykselle: vikatyyppi ja kuvaus, pöytäkirjan numero valinnainen. Tarkastajaksi tulee kirjaaja (palvelin).
   Toni 16.10.2026 klo 05.20 kumosi 15.10. linjauksen «VT:llekin»: VT ei tuota pöytäkirjaa, eikä «VT ✓» kysy numeroa. */
export function VtHylkays({ h, sarakkeita, valmis, peru, tulos = "hylatty" }: { h: Hitsi; sarakkeita: number; valmis: () => void; peru: () => void; tulos?: "hyvaksytty" | "hylatty" }) {
  const hyl = tulos === "hylatty";
  const [vika, setVika] = useState<VtVika | "">("");
  const [syy, setSyy] = useState("");
  const [raportti, setRaportti] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);
  const vikaOk = !hyl || (!!vika && (vika !== "muu" || !!syy.trim()));
  const voiTallentaa = vikaOk && !tallennetaan;
  async function tallenna() {
    setTallennetaan(true); setVirhe(null);
    try {
      await api.kirjaaVt({ raportti: raportti.trim() || undefined,
        rivit: [hyl ? { hitsi_id: h.id, tulos: "hylatty", vika: vika as VtVika, syy: syy.trim() || undefined } : { hitsi_id: h.id, tulos: "hyvaksytty" }] });
      valmis();
    }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("vt.virhe")); }
    finally { setTallennetaan(false); }
  }
  return (
    <tr className="tbl__laajennus" data-vt-hylkays={h.id} data-vt-pika={tulos}>
      <td colSpan={sarakkeita}>
        <div className="vt-hylkays">
          <strong>{hyl ? t("vt.hylkays_otsikko", { nro: h.hitsinumero }) : t("vt.hyvaksynta_otsikko", { nro: h.hitsinumero })}</strong>
          <label>{t("vt.raportti")}
            <input value={raportti} maxLength={200} onChange={(e) => setRaportti(e.target.value)} data-vt-pika-raportti="" />
          </label>
          {hyl && <><label>{t("vt.vika")}
            <select value={vika} onChange={(e) => setVika(e.target.value as VtVika)} data-vt-vika="">
              <option value="">{t("vt.valitse_vika")}</option>
              {VT_VIAT.map((v) => <option key={v} value={v}>{vikaNimi(v)}</option>)}
            </select>
          </label>
          <label>{vika === "muu" ? t("vt.kuvaus_pakollinen") : t("vt.kuvaus")}
            <input value={syy} maxLength={300} onChange={(e) => setSyy(e.target.value)} data-vt-syy="" />
          </label></>}
          <div className="vt-hylkays__napit">
            <button type="button" className="nappi" disabled={!voiTallentaa} onClick={() => void tallenna()} data-vt-hylkays-tallenna="">
              {hyl ? t("vt.tallenna_hylatty") : t("vt.tallenna_hyvaksytty")}</button>
            <button type="button" className="nappi nappi--kevyt" onClick={peru}>{t("hl.peruuta")}</button>
            {!voiTallentaa && !tallennetaan && <span className="dim">{!vika ? t("vt.valitse_vika") : t("vt.kuvaus_pakollinen")}</span>}
          </div>
          {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
        </div>
      </td>
    </tr>
  );
}

type Rivi = { tulos: "hyvaksytty" | "hylatty"; vika: VtVika | ""; syy: string };

export function VtJoukkoIkkuna({ hitsit, kayttaja, sulje, valmis }: {
  hitsit: Hitsi[]; kayttaja: Kayttaja; sulje: () => void; valmis: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  const [tarkastaja, setTarkastaja] = useState(kayttaja.nimi ?? "");
  const [paiva, setPaiva] = useState(tanaan());
  const [raportti, setRaportti] = useState("");
  const [rivit, setRivit] = useState<Record<number, Rivi>>(() =>
    Object.fromEntries(hitsit.map((h) => [h.id, { tulos: "hyvaksytty", vika: "", syy: "" } as Rivi])));
  const [tulos, setTulos] = useState<{ hyvaksytty: number; hylatty: number; ohitetut: { hitsi_id: number; hitsinumero: string | null; syy: string }[] } | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);
  const aseta = (id: number, m: Partial<Rivi>) => setRivit((r) => ({ ...r, [id]: { ...r[id]!, ...m } }));
  const hyl = hitsit.filter((h) => rivit[h.id]?.tulos === "hylatty");
  const puuttuu = hyl.filter((h) => !rivit[h.id]!.vika || (rivit[h.id]!.vika === "muu" && !rivit[h.id]!.syy.trim()));
  async function tallenna() {
    setTallennetaan(true); setVirhe(null);
    try {
      const v = await api.kirjaaVt({ paiva, tarkastaja: tarkastaja.trim() || undefined, raportti: raportti.trim() || undefined,
        rivit: hitsit.map((h) => { const r = rivit[h.id]!; return r.tulos === "hylatty"
          ? { hitsi_id: h.id, tulos: "hylatty" as const, vika: r.vika as VtVika, syy: r.syy.trim() || undefined }
          : { hitsi_id: h.id, tulos: "hyvaksytty" as const }; }) });
      setTulos(v);
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("vt.virhe")); }
    finally { setTallennetaan(false); }
  }
  const sulkeutuu = () => { ref.current?.close(); if (tulos) valmis(); else sulje(); };
  return (
    <dialog ref={ref} className="ikkuna vt-ikkuna" data-vt-ikkuna="" onCancel={(e) => { e.preventDefault(); sulkeutuu(); }}>
      <div className="ikkuna__sisalto">
        <div className="ikkuna__yla">
          <strong>{t("vt.ikkuna_otsikko", { n: hitsit.length })}</strong>
          <button type="button" className="ikkuna__sulje" onClick={sulkeutuu} aria-label={t("hl.sulje")}>×</button>
        </div>
        {tulos ? (
          <div data-vt-tulos={`${tulos.hyvaksytty}/${tulos.hylatty}/${tulos.ohitetut.length}`}>
            <p>{t("vt.kirjattu", { h: tulos.hyvaksytty, x: tulos.hylatty })}</p>
            {tulos.ohitetut.length > 0 && <>
              <p className="dim">{t("vt.ohitettu", { n: tulos.ohitetut.length })}</p>
              <ul className="vt-ohitetut">{tulos.ohitetut.map((o) => (
                <li key={o.hitsi_id} data-vt-ohitettu={o.syy}>{o.hitsinumero ?? o.hitsi_id} — {
                  o.syy === "jo_vt" ? t("vt.jo_kirjattu") : o.syy === "hitsaamaton" ? t("vt.ei_hitsattu") : t("vt.ei_loydy")}</li>))}</ul>
            </>}
            <button type="button" className="nappi" onClick={sulkeutuu}>{t("hl.sulje")}</button>
          </div>
        ) : (<>
          <div className="vt-ikkuna__kentat">
            <label>{t("vt.tarkastaja")}<input value={tarkastaja} maxLength={80} onChange={(e) => setTarkastaja(e.target.value)} /></label>
            <label>{t("vt.paiva")}<input type="date" value={paiva} onChange={(e) => setPaiva(e.target.value)} /></label>
            <label>{t("vt.raportti")}<input value={raportti} maxLength={200} onChange={(e) => setRaportti(e.target.value)} /></label>
          </div>
          {hitsit[0] && <PoytakirjaLiite hitsiId={hitsit[0].id} numero={raportti} />}
          <ul className="vt-ikkuna__lista">
            {hitsit.map((h) => { const r = rivit[h.id]!; return (
              <li key={h.id} data-vt-rivi={h.id} data-vt-rivi-tulos={r.tulos}>
                <span className="mono">{h.linja} · {naytaHitsinumero(h.hitsinumero) || "—"}</span>
                <span className="vt-ikkuna__tulos" role="group">
                  <button type="button" className={"nappi nappi--kevyt" + (r.tulos === "hyvaksytty" ? " vt-valittu" : "")}
                          aria-pressed={r.tulos === "hyvaksytty"} onClick={() => aseta(h.id, { tulos: "hyvaksytty" })}>{t("vt.hyvaksytty")}</button>
                  <button type="button" className={"nappi nappi--kevyt" + (r.tulos === "hylatty" ? " vt-valittu vt-valittu--hyl" : "")}
                          aria-pressed={r.tulos === "hylatty"} onClick={() => aseta(h.id, { tulos: "hylatty" })} data-vt-rivi-hylkaa={h.id}>{t("vt.hylatty")}</button>
                </span>
                {r.tulos === "hylatty" && (
                  <span className="vt-ikkuna__vika">
                    <select value={r.vika} onChange={(e) => aseta(h.id, { vika: e.target.value as VtVika })} aria-label={t("vt.vika")} data-vt-rivi-vika={h.id}>
                      <option value="">{t("vt.valitse_vika")}</option>
                      {VT_VIAT.map((v) => <option key={v} value={v}>{vikaNimi(v)}</option>)}
                    </select>
                    <input value={r.syy} maxLength={300} placeholder={r.vika === "muu" ? t("vt.kuvaus_pakollinen") : t("vt.kuvaus")}
                           onChange={(e) => aseta(h.id, { syy: e.target.value })} aria-label={t("vt.kuvaus")} />
                  </span>
                )}
              </li>); })}
          </ul>
          <p data-vt-yhteenveto={`${hitsit.length - hyl.length}/${hyl.length}`}><strong>{t("vt.yhteenveto", { h: hitsit.length - hyl.length, x: hyl.length })}</strong></p>
          {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
          <div className="vt-ikkuna__napit">
            <button type="button" className="nappi" disabled={tallennetaan || puuttuu.length > 0} onClick={() => void tallenna()} data-vt-tallenna="">
              {t("vt.tallenna")}</button>
            <button type="button" className="nappi nappi--kevyt" onClick={sulkeutuu}>{t("hl.peruuta")}</button>
            {puuttuu.length > 0 && <span className="dim">{t("vt.vika_puuttuu", { n: puuttuu.length })}</span>}
          </div>
        </>)}
      </div>
    </dialog>
  );
}
